<!-- eslint-disable vue/no-unused-vars -->
<script setup lang="ts">
import AppState, { getNestedCustomValues } from '@/states/app'
import {
  CheckIcon,
  Trash01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import {
  AllExtensions,
  RequiredExtensions,
  UIRichTextEditor,
} from '@gohighlevel/ghl-text-editor'
import {
  UIButton,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIRadio,
  UISelect
} from '@gohighlevel/ghl-ui'
import { History } from '@tiptap/extension-history'
import { Editor } from '@tiptap/vue-3'
import DOMPurify from 'dompurify'
import { debounce } from 'lodash-es'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { CalendarService } from '../../class/services/CalendarService'
import {
  LogoShape
} from '../../models/calendar'
import {
  ALLOWED_ATTR_FOR_DESCRIPTION,
  EVENT_COLORS,
} from '../../utils/constants'
import {
  calendarNameFieldOptions,
  handleNestedCustomValueModification,
  meetingInviteTitleFieldOptionsEvent,
  meetingInviteTitleFieldOptionsV3,
  meetingLocationFieldOptionsEvent,
} from '../../utils/customValueMapper'
import { getTooltip } from '../../utils/generalFunctions'
import HeaderTile from '../common/HeaderTile.vue'
import ImageUpload from '../common/ImageUpload.vue'
import TextEditorMenu from '../common/TextEditorMenu.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'

const teamMemberPicker = ref()
const isAliasFieldEnabled = ref<number[]>([])
const formRef = ref()
const { t } = useI18n()
const toast = useToast()

const editor = ref<any>(null)

const state = reactive({
  isLoading: false,
  isSlugValidating: false,
  hasUserModifiedSlug: false,
  formData: {
    name: '',
    eventColor: '#D50000',
    eventTitle: '',
    groupId: '',
    description: '',
    widgetSlug: '',
    fileList: [],
    widgetLogo: {
      shape: LogoShape.SQUARE,
      url: '',
    },
  }
})

const intializeEditor = () => {
  editor.value = new Editor({
    content: editingCalendar.value?.description || '',
    parseOptions: {
      preserveWhitespace: 'full',
    },
    extensions: [
      History,
      RequiredExtensions,
      AllExtensions.configure({
        placeholder: {
          placeholder: t('create_calendar.description_placeholder'),
          showForAllNodes: false,
        },
        divTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        preTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        textAlignment: { types: ['heading', 'paragraph'] },
        paragraph: {
          HTMLAttributes: {
            style: 'margin:0px;color:#10182899',
          },
        },
        heading: {
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        link: {
          HTMLAttributes: {
            target: '_blank',
          },
        },
      }),
    ],
    onUpdate({ editor }) {
      state.formData.description = editor.getHTML()
    },
  })
}

const groupsOptions = computed(() => {
  return (AppState.globalTeams as any)?.teams?.map((x: any) => {
    return {
      value: x.id,
      label: x.name,
    }
  })
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const emit = defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    if (editingCalendar.value && !editor.value) {
      intializeEditor()
    }

    state.formData.name = editingCalendar.value?.name || ''
    state.formData.description = editingCalendar.value?.description || ''
    state.formData.groupId = editingCalendar.value?.groupId || ''
    state.formData.widgetSlug = editingCalendar.value?.widgetSlug || ''
    state.formData.eventTitle = editingCalendar.value?.eventTitle || ''

    state.formData.eventColor = editingCalendar.value?.eventColor || '#D50000'

    state.formData.widgetLogo.url = editingCalendar.value?.widgetLogo?.url || ''
    state.formData.widgetLogo.shape =
      editingCalendar.value?.widgetLogo?.shape || LogoShape.SQUARE
  },
  async handleUploadFromSystem(file: any) {
    const formData = new FormData()
    formData.append('logo', file.file)
    const {
      data: { uploadedUrl },
    } = await CalendarService.uploadCalendarLogo(
      editingCalendar.value!.id,
      formData,
      editingCalendar?.value?.locationId || AppState.locationId
    )

    state.formData.widgetLogo.url = uploadedUrl
  },
  openMediaLibrary() {
    AppState.mediaLibraryFieldType = 'calendarLogo'
    AppState.parentApp?.then(parent => {
      parent.emit('media-library-open', {
        apiKey: '',
        tokenId: AppState.tokenId,
        companyId: AppState.companyId,
        altId: AppState.locationId,
        altType: 'location',
        hideHeader: true,
        allowedExtensions: ['image'],
        maxUploadSize: 50000000,
        category: 'calendars',
        subCategory: 'calendar_settings',
      })
      AppState.isMediaLibraryOpen = true
    })
  },
  async removeLogo() {
    state.formData.widgetLogo.url = ''
  },
  async emitSave() {
    try {
      await formRef.value.getForm().validate()
      isAliasFieldEnabled.value = []
    } catch (error) {
      return
    }
    const sanitizedDescription = DOMPurify.sanitize(
      state.formData.description,
      {
        ADD_ATTR: ALLOWED_ATTR_FOR_DESCRIPTION,
      }
    )
    const payload = {
      name: state.formData.name,
      description: sanitizedDescription,
      groupId: state.formData.groupId,
      widgetSlug: state.formData.widgetSlug,
      eventTitle: state.formData.eventTitle,
      eventColor: state.formData.eventColor,
      widgetLogo: state.formData.widgetLogo
    } as Record<string, any>

    if (
      editingCalendar.value?.widgetLogo.url &&
      editingCalendar.value?.widgetLogo.url !== state.formData.widgetLogo.url
    ) {
      await CalendarService.removeCalendarLogo(
        editingCalendar.value!.id,
        AppState.locationId,
        { imageUrl: editingCalendar.value?.widgetLogo?.url }
      )
    }

    return payload
  },

  openAliasField(index: number) {
    isAliasFieldEnabled.value.push(index)
  },
  closeAliasField(index: number) {
    isAliasFieldEnabled.value = isAliasFieldEnabled.value.filter(
      x => x !== index
    )
  },
  /**
   * Generate calendar slug based on calendar name
   */
  async generateSlugFromName(calendarName: string) {
    // Only generate slug if:
    // 1. Calendar name exists
    // 2. Either user hasn't manually modified the slug OR the slug is currently empty
    // 3. We're in create mode (not edit mode)
    if (
      !calendarName?.trim() ||
      (state.hasUserModifiedSlug && state.formData.widgetSlug?.trim()) ||
      !AppState.isNewCalendar || // Only for new calendars (create mode)
      editingCalendar.value?.id // Don't auto-generate if calendar already has an ID
    ) {
      return
    }

    state.isSlugValidating = true
    try {
      // Convert calendar name to slug format (lowercase, replace spaces with hyphens, remove special chars)
      let generatedSlug = calendarName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens

      // Check if slug is available
      const { data } = await CalendarService.isValidCalendarSlug(
        generatedSlug,
        editingCalendar.value?.id
      )

      if (data?.available) {
        state.formData.widgetSlug = generatedSlug
      } else {
        // If slug is taken, append a number
        let counter = 1
        let slugWithNumber = `${generatedSlug}-${counter}`
        let isAvailable = false

        while (!isAvailable && counter < 100) {
          const { data: checkData } = await CalendarService.isValidCalendarSlug(
            slugWithNumber,
            editingCalendar.value?.id
          )
          if (checkData?.available) {
            state.formData.widgetSlug = slugWithNumber
            isAvailable = true
          } else {
            counter++
            slugWithNumber = `${generatedSlug}-${counter}`
          }
        }
      }
    } catch (error) {
      console.error('Error generating slug:', error)
    } finally {
      state.isSlugValidating = false
    }
  },
}

defineExpose({
  methods,
})

// Debounced slug validation function
const validateSlugDebounced = debounce(
  async (
    slug: string,
    calendarId: string | undefined,
    resolve: (value: boolean) => void,
    reject: (error: Error) => void
  ) => {
    try {
      const { data } = await CalendarService.isValidCalendarSlug(
        slug,
        calendarId
      )
      state.isSlugValidating = false
      if (!data?.available) {
        return reject(new Error('This slug is already taken'))
      }
      return resolve(true)
    } catch (error) {
      state.isSlugValidating = false
      return reject(error as Error)
    }
  },
  500
)

const rules = computed(() => {
  return {
    name: {
      validator() {
        if (!state.formData.name?.trim()) {
          return new Error('Please enter a name')
        }

        return true
      },
      message: t('calendar_advanced.meeting_details.enter_a_name'),
      trigger: 'blur',
    },
    eventTitle: {
      required: true,
      message: t('calendar_advanced.meeting_details.enter_an_event_title'),
      trigger: 'blur',
    },
    widgetSlug: {
      required: true,
      trigger: ['input', 'blur'],
      asyncValidator() {
        return new Promise(async (resolve, reject) => {
          state.isSlugValidating = true

          if (!state.formData.widgetSlug) {
            state.isSlugValidating = false
            return reject(new Error(t('validation_messages.slug_required')))
          }

          const slugRegex = /^[a-z0-9-_]+(\/[a-z0-9-_]+)*$/

          if (!slugRegex.test(state.formData.widgetSlug)) {
            state.isSlugValidating = false
            return reject(new Error('This slug is not valid'))
          }

          // Use debounced API call
          validateSlugDebounced(
            state.formData.widgetSlug,
            editingCalendar.value?.id,
            resolve,
            reject
          )
        })
      },
    },
  }
})

const calendarNameValuesRef = ref(calendarNameFieldOptions)
const meetingInviteTitleRef = ref(meetingInviteTitleFieldOptionsV3)
const meetingLocationEventRef = ref(meetingLocationFieldOptionsEvent)

// Function to load and update custom values with nested folder structure
const updateCustomValuesRefs = async () => {
  const meetingInviteTitleOptions = editingCalendar.value?.isEventCalendar
    ? meetingInviteTitleFieldOptionsEvent
    : meetingInviteTitleFieldOptionsV3

  // Set base options first (synchronous)
  calendarNameValuesRef.value = calendarNameFieldOptions
  meetingLocationEventRef.value = meetingLocationFieldOptionsEvent
  meetingInviteTitleRef.value = meetingInviteTitleOptions

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    calendarNameValuesRef.value = [...calendarNameFieldOptions, customValues]
    meetingLocationEventRef.value = [
      ...meetingLocationFieldOptionsEvent,
      customValues,
    ]
    meetingInviteTitleRef.value = [...meetingInviteTitleOptions, customValues]
  }
}

// Watch only when calendar ID changes to avoid unnecessary API calls
watch(
  () => editingCalendar.value?.id,
  newId => {
    if (newId) {
      updateCustomValuesRefs()
    }
  },
  { immediate: true }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  },
  { immediate: true }
)

watch(
  () => AppState.mediaFileURI,
  () => {
    if (AppState.mediaFileURI) {
      if (AppState.mediaLibraryFieldType === 'calendarLogo') {
        state.formData.widgetLogo.url = AppState.mediaFileURI
        AppState.mediaLibraryFieldType = ''
        AppState.mediaFileURI = ''
      }
    }
  }
)

// Update calendar name in real-time as user types
watch(
  () => state.formData.name,
  newName => {
    if (editingCalendar.value) {
      editingCalendar.value.name = newName
    }
  }
)

// Create debounced function for generating slug
const debouncedGenerateSlug = debounce(methods.generateSlugFromName, 800)

// Watch for calendar name changes to auto-generate slug (only in create mode if slug is not already set)
watch(
  () => state.formData.name,
  newName => {
    if (
      newName?.trim() &&
      AppState.isNewCalendar &&
      !AppState.editingCalendar?.widgetSlug
    ) {
      debouncedGenerateSlug(newName)
    }
  }
)

// Watch for manual slug modifications
watch(
  () => state.formData.widgetSlug,
  (newSlug, oldSlug) => {
    // Mark as user-modified if slug changes and it's not empty
    // (don't mark as modified when auto-generating from empty)
    if (newSlug !== oldSlug && oldSlug !== undefined && oldSlug !== '') {
      state.hasUserModifiedSlug = true
    }
  }
)

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const insertMeetingInviteTitle = (value: string) => {
  state.formData.eventTitle = state.formData.eventTitle + ' ' + value
}

const insertCalendarName = (value: string) => {
  state.formData.name = state.formData.name + ' ' + value
}
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <div class="flex flex-row items-end justify-between gap-2">
      <HeaderTile
        :header="
          isServiceCalendar
            ? $t('calendar_advanced.meeting_details.service_details')
            : $t('calendar_advanced.meeting_details.meeting_details')
        "
        :description="
          t('calendar_advanced.meeting_details.meeting_details_description')
        "
        :show-calendar-id="true"
        :calendar-id="editingCalendar?.id"
      />
    </div>

    <div class="my-[15px] border-t border-gray-200" />

    <UIForm
      id="create-calendar-form"
      ref="formRef"
      :model="state.formData"
      :rules="rules"
      class="w-full"
    >
      <UIFormItem
        :label="
          isServiceCalendar
            ? $t('create_calendar.service_logo')
            : $t('create_calendar.calendar_logo')
        "
        :tooltip="
          getTooltip(
            $t('calendar_advanced.meeting_details.calendar_logo_tooltip')
          )
        "
      >
        <div v-if="!state.formData.widgetLogo.url" class="w-full">
          <ImageUpload
            @uploadFromSystem="methods.handleUploadFromSystem"
            @chooseFromMediaLibrary="methods.openMediaLibrary"
          />
        </div>
        <div v-else class="flex items-start space-x-10">
          <img
            :src="state.formData.widgetLogo.url"
            class="h-40 w-40 object-fill"
            :class="{
              'rounded-full':
                state.formData.widgetLogo.shape === LogoShape.ROUND,
              'rounded-lg':
                state.formData.widgetLogo.shape === LogoShape.SQUARE,
            }"
            :alt="'create-logo'"
          />

          <div class="flex flex-col justify-start">
            <UIFormItem
              :label="$t('calendar_advanced.meeting_details.widget_shape')"
            >
              <div class="flex w-full flex-col space-y-4">
                <div class="flex space-x-4">
                  <UIRadio
                    id="calendar-widget-logo-square"
                    :checked="
                      state.formData.widgetLogo.shape === LogoShape.SQUARE
                    "
                    :value="LogoShape.SQUARE"
                    :name="LogoShape.SQUARE"
                    @change="state.formData.widgetLogo.shape = LogoShape.SQUARE"
                  >
                    {{ $t('calendar_advanced.meeting_details.square') }}
                  </UIRadio>

                  <UIRadio
                    id="calendar-widget-logo-round"
                    :checked="
                      state.formData.widgetLogo.shape === LogoShape.ROUND
                    "
                    :value="LogoShape.ROUND"
                    :name="LogoShape.ROUND"
                    @change="state.formData.widgetLogo.shape = LogoShape.ROUND"
                  >
                    {{ $t('calendar_advanced.meeting_details.circle') }}
                  </UIRadio>
                </div>
              </div>
            </UIFormItem>

            <UIButton
              id="calendar-logo-remove-btn"
              class="border-red-300 text-red-700"
              @click="methods.removeLogo"
            >
              <Trash01Icon class="mr-2 w-5" />
              <div class="">
                {{ $t('calendar_advanced.meeting_details.remove_logo') }}
              </div>
            </UIButton>
          </div>
        </div>
      </UIFormItem>
      <UIFormItem
        :label="
          isServiceCalendar
            ? $t('create_calendar.service_calendar_name')
            : $t('create_calendar.calendar_name')
        "
        :tooltip="
          getTooltip(
            $t('calendar_advanced.meeting_details.calendar_name_tooltip')
          )
        "
        path="name"
      >
        <UIInput
          id="calendar-name-input"
          v-model="state.formData.name"
          :placeholder="$t('create_calendar.eg_outbound_reach')"
        >
          <template #suffix>
            <CustomVariablePickerV2
              :dropdown-options="calendarNameValuesRef"
              @update:selected-value="val => insertCalendarName(val)"
            />
          </template>
        </UIInput>
      </UIFormItem>

      <UIFormItem
        :label="$t('create_calendar.description')"
        :tooltip="
          getTooltip(
            $t('calendar_advanced.meeting_details.description_tooltip')
          )
        "
        :show-feedback="false"
      >
        <div class="flex w-full flex-col">
          <TextEditorMenu :editor="editor" />
          <UIRichTextEditor
            :editor="editor"
            container-class="force-border-0 overflow-x-auto custom-editor-styling"
          />
        </div>
      </UIFormItem>
      <div class="my-[20px] border-t border-gray-200" />

      <UIFormItem
        class="w-full"
        :label="$t('create_calendar.custom_url')"
        path="widgetSlug"
        :tooltip="
          getTooltip($t('calendar_advanced.meeting_details.custom_url_tooltip'))
        "
      >
        <UIInputGroup>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <UIInputGroupLabel>/widget/bookings/</UIInputGroupLabel>
          <UIInput
            id="calendarUrl"
            :model-value="state.formData.widgetSlug"
            :placeholder="t('settings.calendar.my_calendar')"
            @update:model-value="
              state.formData.widgetSlug = $event.toLowerCase()
            "
          />
        </UIInputGroup>
      </UIFormItem>

      <div class="flex w-full flex-row items-start gap-4">
        <UIFormItem
          class="w-1/2"
          :label="$t('create_calendar.group')"
          :tooltip="
            getTooltip($t('calendar_advanced.meeting_details.group_tooltip'))
          "
        >
          <UISelect
            id="calendar-group-select"
            v-model:value="state.formData.groupId"
            :options="groupsOptions"
            :filterable="true"
          />
        </UIFormItem>

        <UIFormItem
          class="w-1/2"
          :label="
            isServiceCalendar
              ? $t('create_calendar.appointment_invite_title')
              : $t('create_calendar.meeting_invite_title')
          "
          :tooltip="
            getTooltip(
              $t(
                'calendar_advanced.meeting_details.meeting_invite_title_tooltip'
              )
            )
          "
          path="eventTitle"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <UIInput
            id="calendar-event-title-input"
            v-model="state.formData.eventTitle"
            :placeholder="'{{contact.name}}'
            "
          >
            <template #suffix>
              <CustomVariablePickerV2
                :dropdown-options="meetingInviteTitleRef"
                @update:selected-value="val => insertMeetingInviteTitle(val)"
              />
            </template>
          </UIInput>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        </UIFormItem>
      </div>

      <UIFormItem
        class="mt-4"
        :label="$t('create_calendar.meeting_color')"
        :tooltip="
          getTooltip(
            $t('calendar_advanced.meeting_details.meeting_color_tooltip')
          )
        "
      >
        <div
          v-for="color in EVENT_COLORS"
          :key="color.colorName"
          class="mr-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border"
          :style="{
            'background-color': color.colorHexCode,
          }"
          role="button"
          :aria-labelledby="'event-color-' + color.colorName"
          :title="color.colorName"
          :aria-label="color.colorHexCode"
          @click="state.formData.eventColor = color.colorHexCode"
          @keypress="state.formData.eventColor = color.colorHexCode"
          tabindex="0"
        >
          <CheckIcon
            v-if="state.formData.eventColor === color.colorHexCode"
            class="h-5 w-5 text-white"
          />
        </div>
      </UIFormItem>
    </UIForm>
  </div>
</template>

<style lang="scss">
.ui-upload-container .upload-icon svg {
  padding: 0px !important;
}

#create-calendar-form {
  .force-border-0 {
    border: 1px solid #d0d5dd !important;
    padding: 15px !important;
    max-height: 200px !important;
    min-height: 100px !important;
  }

  .ProseMirror p {
    margin: 0;
    font-size: 13px !important;
  }

  .ProseMirror ol {
    font-size: 14px !important;
  }

  .v3-search input::placeholder {
    font-size: 1rem;
    text-align: left;
    font-weight: normal;
    font-size: 0.875rem;
  }
}
</style>
